// Long-form text for the end of each ownezzy.com page (shown by components/SeoArticle.js). Written around the
// searches in the Super Admin SEO tab (apps/admin/src/data/seoKeywords200.js): each page covers the searches that
// point to it. Keep every claim true to the product: prices from the live plans, features from the admin panel.

export const HOME_ARTICLE = {
  id: 'about-ownezzy-websites',
  title: 'How to create a website for your shop or store',
  lead: 'Ownezzy is an online shopping website maker for shopkeepers, home businesses and Instagram sellers in India. You pick a ready-made website for what you sell, change it to your own, and start taking orders. There is no coding, no designer and no setup fee, and every store starts with a 14-day free trial. That makes it a simple ecommerce website for Indian merchants who want to sell their products online with a free website first and pay only when it works for them.',
  sections: [
    {
      h: 'Get a website for your shop free, in about a minute',
      p: [
        'If you are wondering how to create a website for your shop or how to create a website for your store, the answer on Ownezzy is short. Choose a design from 100+ ready-made websites, type your store name, WhatsApp number and email, and enter the code we email you. That is enough to open your ecommerce store in about 60 seconds, which is why shop owners say it is how to create a store website in 1 minute.',
        'Your new store already has a home page, categories, sample products, a cart and checkout. You replace the sample products with your own photos and prices from the admin panel, on your phone or computer. It is the easiest ecommerce website builder for beginners because everything is already in place: you only change what is yours. Read the step-by-step [User Guide](/guide) to see every screen.',
      ],
    },
    {
      h: 'The best website builder for a retail shop in India',
      p: [
        'A neighbourhood shop needs different things from a big brand. You want to take orders on WhatsApp, collect money by UPI or Cash on Delivery, add GST and a delivery charge, and share one link with your regular customers. Ownezzy is built for exactly that, which makes it a strong choice of website builder for a retail shop in India and a quick way to make a website for a local retail store.',
        'Think of it as a digital storefront for your neighbourhood shop: your customers see what you have in stock, its price and photos, and order from home. Kirana stores, boutiques, gift shops, bakeries and many more use it to create a digital storefront without changing how they run their shop.',
      ],
    },
    {
      h: 'A website for Instagram sellers, with payment in your bio link',
      p: [
        'For Instagram sellers, your Ownezzy store works as an Instagram bio link store. Put your store address in the Website field of your profile and every follower can open your full catalogue, choose a size and pay, without sending you a DM. It is a free website for Instagram sellers to try for 14 days, and a website link in your Instagram bio with payment built in.',
        'Each product has its own link, so you can sell products on Instagram with a website link in posts, reels and stories. A direct checkout link for an Instagram story takes a follower straight to the product page. Because orders arrive complete with the size, address and payment, it is the simplest way to automate Instagram orders for small sellers. Customers can also see the status of each order in My orders, so your Instagram bio website comes with order tracking too. It is an Instagram shop website builder you can set up free in India in a few minutes.',
      ],
    },
    {
      h: 'WhatsApp ordering website with instant order alerts',
      p: [
        'Many Indian shops already sell on WhatsApp but spend hours typing prices and addresses. With a WhatsApp ordering website for Indian shops, customers place the order on your website and you get an instant WhatsApp notification for every new order, with a 🔔 alert in the admin panel as well. You automate WhatsApp orders for your small business without changing how you talk to customers.',
        'Your store link is a WhatsApp shop link you can make free and share in chats, groups and your WhatsApp status. Every store has a WhatsApp button, so it is also a free website maker with a WhatsApp button for customers who prefer to ask first. One link works as a WhatsApp and Instagram online store link, and many owners call it their online dukaan on WhatsApp.',
      ],
    },
    {
      h: 'Direct UPI payments with PhonePe and Google Pay checkout',
      p: [
        'Ownezzy is an online store with PhonePe and Google Pay checkout. You choose how to get paid. With manual UPI, customers pay straight to your own UPI ID, so it is a direct UPI payment website for your shop: the money reaches your account the moment the customer pays, with zero delay and no gateway in between. Because the payment goes to your UPI ID, your Paytm, PhonePe or Google Pay soundbox announces it just like a payment at your counter, which is why some call it a Paytm soundbox UPI store builder.',
        'If you prefer automatic payment confirmation, connect your own Razorpay account and customers can pay by UPI, card or net banking at checkout, with the money settled to your bank. Cash on Delivery works alongside both. See [Store settings in the User Guide](/guide/store-settings) to set it up.',
      ],
    },
    {
      h: 'One account for many stores, and a 14-day free trial',
      p: [
        'If you run more than one business, you can manage multiple online stores from a single account. Every store has its own products, orders, payments and address, and you switch between them from My websites in the admin panel.',
        'Every new store starts with a free trial, so Ownezzy is a small business website creator with a free trial and a retail shop website maker with a 14-day trial. It is also a home business website maker you can start free from your phone. After the trial, keep your store for ₹399 a month or ₹4,499 a year, with 0% commission on your sales.',
      ],
    },
  ],
};

export const PLANS_ARTICLE = {
  id: 'plans-explained',
  title: 'Zero commission ecommerce platform for India',
  lead: 'Ownezzy plans are simple: a 14-day free trial, then ₹399 a month or ₹4,499 a year for your store. You sell online with 0 percent commission, so every rupee of your sales stays with you.',
  sections: [
    {
      h: 'An ecommerce website for ₹399 per month',
      p: [
        'The Monthly plan is an ecommerce website for ₹399 per month: your own store website, the admin panel, orders, payments and WhatsApp alerts. The Yearly plan costs ₹4,499 a year, about ₹375 a month. New customers may also see a first-month offer at checkout.',
        'Ownezzy is a zero commission ecommerce platform in India. Marketplaces take a cut of every order; with your own store you pay only the plan, whatever you sell.',
      ],
    },
    {
      h: '0 gateway fee and direct bank settlement',
      p: [
        'With manual UPI there is no payment gateway, so it is an ecommerce website with 0 gateway fee: customers pay your UPI ID directly. If you connect Razorpay for automatic payments, Razorpay charges its own fee and settles the money directly to your bank account, never through Ownezzy, so it also works as a direct bank settlement website builder.',
      ],
    },
    {
      h: 'Your own domain, or a free ownezzy.com address',
      p: [
        'Every store gets a free address like yourstore.ownezzy.com. To build an online store with a custom domain in India, such as yourstore.com or yourstore.in, choose a Monthly or Yearly plan and pick Main domain at checkout. The domain has its own yearly fee, shown before you pay, or you can buy one yourself and we link it for you.',
      ],
    },
  ],
};

export const WEBSITES_ARTICLE = {
  id: 'about-ready-made-websites',
  title: 'Ready-made store websites for every business',
  lead: 'Every website on this page is a complete online store you can make your own. If you are asking how to create a website for your business free, start here: open any website as a live demo, then start a 14-day free trial on it. Ownezzy is a free online store maker for India with 100+ designs for shops, boutiques, home businesses and Instagram sellers.',
  sections: [
    {
      h: 'Try a live demo, with instant checkout',
      p: [
        'Each card opens a live demo you can shop in like a customer. Every store is a retail website with instant checkout: add to cart, choose a size, and pay by UPI, card or Cash on Delivery. At checkout customers can pay with any UPI app or scan a UPI QR code. It is the clearest way to see a WhatsApp ecommerce store demo before you choose.',
        'Ownezzy is a free website builder with UPI payment and a simple UPI-based ecommerce website builder: you add your UPI ID or your own Razorpay account and customers pay you directly.',
      ],
    },
    {
      h: 'A quick store builder for physical retailers',
      p: [
        'If you already have a shop, you do not need to start from scratch. Pick the website closest to what you sell and your online store is live in about a minute, which makes Ownezzy a quick store builder for physical retailers and a free shopping site creator in India. Share the link with regular customers and take orders for delivery or pickup.',
      ],
    },
    {
      h: 'A website for your Instagram shop',
      p: [
        'Wondering how to create a website for your Instagram shop? Choose a design, add your products and put the link in your bio. Followers get an Instagram bio shop with direct payment and a free catalog link for your Instagram page, sorted by category. Resellers can import many products at once, so it also works as an Instagram reseller store website builder. For many sellers it is the best website platform for an Instagram business: social commerce in India without marketplace fees.',
      ],
    },
    {
      h: 'Built for WhatsApp orders',
      p: [
        'Every website here is made to create a website for WhatsApp orders. Share your store as a free catalog website for WhatsApp sharing, and customers send orders through the website instead of long chats. Each new order arrives on WhatsApp, so it is an ecommerce store for WhatsApp Business and a WhatsApp store builder for India in one.',
        'You can share a product link on WhatsApp with a buy button: the customer opens the product and orders. It works as a digital catalog with WhatsApp checkout, and as a website builder that sends each order to your WhatsApp, keeping your WhatsApp Business chats and your orders in sync. See [Manage orders](/guide/manage-orders) in the User Guide.',
      ],
    },
    {
      h: 'Websites for every kind of business',
      p: [
        'Clothing, jewellery, home decor, gifts, cakes, groceries, skincare, footwear, phone cases, pet supplies and even an online plant nursery website maker: search for your business above, or tap Create Custom Website if it is not listed. See [Set up a website for your kind of business](/guide/websites-by-business) for tips by business.',
      ],
    },
  ],
};

export const BLOG_ARTICLE = {
  id: 'selling-online-guide',
  title: 'Selling online in India: the short answers',
  lead: 'Quick answers to what shop owners ask most about starting an online store, selling on Instagram and WhatsApp, and getting paid. Each answer links to the step-by-step [User Guide](/guide).',
  sections: [
    {
      h: 'How to make an ecommerce website without coding',
      p: [
        'You do not need to write code or hire a developer to build an e-store without technical skills. Choose a ready-made website on [ownezzy.com/websites](/websites), start a free trial and change the text, photos and prices from simple forms. That is all it takes to make an ecommerce website without coding.',
      ],
    },
    {
      h: 'Dukaan website kaise banaye?',
      p: [
        'Apne shop ki website kaise banaye, yeh bahut aasaan hai: ownezzy.com/websites par apne business ki website chuniye, Start Free Trial dabaiye, store ka naam, WhatsApp number aur email daliye, aur email par aaya code daliye. Aapki online dukaan 1 minute mein ready ho jaati hai. Online dukaan app se website kaise banaye, iska jawab bhi yahi hai: koi app download nahi karna, sab kuch phone ke browser se hota hai.',
        'That is also how to start an online dukaan in India and how to start an online store from home in India: a free trial, your products, and your store link shared on WhatsApp and Instagram.',
      ],
    },
    {
      h: 'How to sell on Instagram without DMs',
      p: [
        'To stop taking orders in Instagram DMs, put your store link in your bio and post product links in your stories. Followers choose the product, size and address and pay on your website, so you sell on Instagram without DMs and get each order complete. This is the Instagram reels to website checkout flow: a reel brings interest, the link brings the order.',
        'To take UPI orders on Instagram, or to add a UPI payment link in your Instagram bio, simply use your store link: UPI checkout is built in. Featured products, offers and coupons help convert Instagram followers into website buyers. If you want to know how to make a website for an Instagram business, see the [website editor guide](/guide/website-editor).',
      ],
    },
    {
      h: 'How to sell on WhatsApp without manual messages',
      p: [
        'To connect your website with WhatsApp orders, add your WhatsApp number when you create your store. Every order is then sent to you on WhatsApp automatically, so you receive customer orders on WhatsApp without typing a word. That is how to automate WhatsApp retail orders and how to sell on WhatsApp without manual messages.',
      ],
    },
    {
      h: 'How to take UPI and COD orders on your website',
      p: [
        'To accept GPay and PhonePe on your website for free, add your UPI ID in manual UPI. Customers pay you from any UPI app, and with Razorpay they can also scan a UPI QR code at checkout. To take COD orders on your website, offer Cash on Delivery at checkout and mark the order paid when you collect the money. See [Store settings](/guide/store-settings).',
      ],
    },
    {
      h: 'How to sell products on Google without Amazon',
      p: [
        'Your own website can show in Google search for your store name and products, so you can sell products on Google without Amazon or any marketplace commission. Set your title and description in [SEO settings](/guide/seo). To get a custom domain for your online shop, pick Main domain with a Monthly or Yearly plan ([Buy a plan](/guide/buy-a-plan)).',
        'Selling through your own link is also how to sell on social media without high fees: Ownezzy takes 0% commission, and manual UPI has no gateway fee.',
      ],
    },
    {
      h: 'The cheapest ecommerce platform for a small business in India',
      p: [
        'Ownezzy costs ₹399 a month or ₹4,499 a year after the 14-day free trial, with a ready-made website, UPI and COD checkout, WhatsApp order alerts and 0% commission. Shop owners looking for a cheap Shopify alternative in India, a Dukaan app alternative with a full website, or a Bikayi alternative with a free trial can try every feature free before paying.',
      ],
    },
  ],
};

export const ABOUT_ARTICLE = {
  id: 'why-ownezzy',
  title: 'Why shop owners choose Ownezzy',
  lead: 'Ownezzy exists so that any shop owner, home business or Instagram seller can have a real website without technical skills, without a big budget and without giving away a share of their sales.',
  sections: [
    {
      h: 'Made for small businesses',
      p: [
        'Most website builders are made for designers. Ownezzy is made for shopkeepers: ready-made websites for 100+ kinds of business, an admin panel that works on a phone, WhatsApp order alerts, UPI and Cash on Delivery, GST and delivery charges, and a step-by-step [User Guide](/guide) for every screen.',
      ],
    },
    {
      h: 'Fair and simple pricing',
      p: [
        'Every store starts with a 14-day free trial. After that it is ₹399 a month or ₹4,499 a year, with 0% commission on your sales. Payments from your customers go to your own UPI ID or your own Razorpay account, never through Ownezzy.',
      ],
    },
  ],
};

// One article per /create/<slug> page
export const NICHE_ARTICLES = {
  'clothing-website': {
    title: 'How to create a website for your boutique or clothing brand',
    lead: 'Whether you run a saree shop, a kurti boutique or a new T-shirt brand, an Ownezzy clothing website gives you sizes, colours, a price for each size and UPI checkout, ready to use. It is a free boutique shop ecommerce template you can try for 14 days.',
    sections: [
      {
        h: 'Sarees, kurtis and suits',
        p: [
          'Create an online store for your saree business, whether you sell cotton sarees, silk sarees or Banarasi sarees direct to customers. The same website works as a handloom boutique website builder and an online store for chikankari kurtis. If you have wondered how to sell dress material online or how to sell stitched suits online in India, add each design as a product with photos, sizes and prices, and share the link.',
          'Kurti and lehenga boutiques use it too: it is a website maker to sell kurtis online and a lehenga boutique online store builder in one. Add your dupattas and stoles, designer blouses and ethnic wear in their own categories, and you can sell ethnic wear online with a free website to begin with.',
        ],
      },
      {
        h: 'Western wear, menswear and activewear',
        p: [
          'The same website is a western wear boutique website you can try free, a menswear retail store website maker, an activewear and gym clothing website builder, and a modest wear or abaya online store maker. A T-shirt brand website maker with a free trial lets a new label launch its first collection before paying anything.',
          'Building a sustainable fashion brand, running an Instagram thrift store, or learning how to sell second hand clothes online in India? Add each piece with clear photos and its size, and let customers buy straight from your link.',
        ],
      },
      {
        h: 'Sizes, size charts and a price for each size',
        p: [
          'A fashion boutique website needs a size chart. Add it as one of the three product photos, or write the measurements in the description. Each size (S, M, L, XL) can have its own price, which is perfect for bridal wear or made-to-measure pieces. Bridal wear boutiques can use their store as an online portfolio of their work, with each outfit as a product.',
          'For a custom tailoring website, add each stitching service as a product; the customer orders on the website and sends their measurements on WhatsApp. Wholesale sellers can share a wholesale clothing catalog website with retailers, with prices per piece or per set.',
        ],
      },
      {
        h: 'Start an online clothing store in India',
        p: [
          'This is how to create a website for boutique clothing and how to start an online clothing store in India: start the free trial, add your products, and share your link on Instagram and WhatsApp. You can create a clothing brand website free for 14 days and pay ₹399 a month only when you decide to keep it. See [Add products](/guide/products) in the User Guide.',
        ],
      },
    ],
  },
  'kids-ethnic-wear-website': {
    title: 'A website for kids ethnic wear',
    lead: 'A kids ethnic wear website maker for boutiques that sell pattu pavadai, lehengas, kurta sets and festive wear for children.',
    sections: [
      {
        h: 'Pattu pavadai and festive kids wear',
        p: [
          'Use it as a pattu pavadai online shop builder: add each outfit with photos and age sizes like 1-2 years or 3-4 years, each with its own price. Parents pick the size and pay by UPI or Cash on Delivery.',
          'Before festivals, feature your best sets on the home page and share a coupon on WhatsApp. See [Featured products and offers](/guide/featured-and-offers).',
        ],
      },
    ],
  },
  'jewelry-website': {
    title: 'How to sell jewellery online in India',
    lead: 'An Ownezzy jewellery website gives you close-up photos, finishes, sizes and instant UPI checkout, ready to use. It is an artificial jewellery store builder and a silver jewellery ecommerce website maker in one.',
    sections: [
      {
        h: 'Artificial, imitation and fashion jewellery',
        p: [
          'Use it as an imitation jewellery catalog website builder, an oxidised jewellery website maker or a kundankari jewellery online store. Temple jewellery sellers can use it as a temple jewellery online shop maker, and bridal sets fit well as a bridal imitation jewellery website template. You can sell fashion jewellery online free during the trial, and it works as a fashion earrings selling website and a hair accessories online store builder.',
        ],
      },
      {
        h: 'Handmade, silver and gold plated jewellery',
        p: [
          'If you want to know how to sell handmade jewellery online in India, add each piece with up to three close-up photos: together they make a photo gallery and order website for your jewellery. It suits beaded jewellery online stores, terracotta jewellery online selling, resin jewellery, and brass and copper jewellery.',
          'For gold plated jewellery, write the plating and care in the description, and use colours for the finishes. For customised name jewellery, a customized name jewellery website builder works best when you ask for the name in the description and confirm it on WhatsApp.',
        ],
      },
      {
        h: 'Start an online jewellery business from home',
        p: [
          'This is how to start an online jewellery business from home: a free trial, your best photos, and your store link in your Instagram bio. Turn on UPI and you have a jewellery boutique website with instant UPI payments. See [Store settings](/guide/store-settings).',
        ],
      },
    ],
  },
  'crochet-handicrafts-website': {
    title: 'How to sell handicrafts and art online in India',
    lead: 'A website for artists and makers: resin art, paintings, crochet, jute bags and handmade stationery, sold directly to your customers.',
    sections: [
      {
        h: 'Resin art, paintings and Madhubani',
        p: [
          'If you are asking how to sell resin art online in India, add each piece with photos and sizes. It works as a resin bookmark and clock website maker. Painters asking how to sell paintings online in India can use it the same way, and it is a Madhubani painting online shop maker too.',
        ],
      },
      {
        h: 'Crochet, jute bags and stationery',
        p: [
          'Sell crochet items online in India, make a jute bags business website, or sell handmade stationery and journals from one store. Your own website is an artisan marketplace alternative for Indian crafts: no commission, and your customers stay with you.',
        ],
      },
    ],
  },
  'home-decor-website': {
    title: 'An online store for home decor and handcrafted products',
    lead: 'A free home decor online store template for candles, pottery, macrame, wooden handicrafts, brass and festive decor.',
    sections: [
      {
        h: 'Candles, pottery and wall hangings',
        p: [
          'Use it as a handmade scented candles website builder, a ceramic pottery online store builder, a blue pottery Jaipur online store builder or a macrame wall hanging website maker. Each product can have sizes with their own prices, such as small, medium and large.',
        ],
      },
      {
        h: 'Wooden, brass and festive decor',
        p: [
          'It is a wooden handicrafts ecommerce website, a brass artifacts online selling platform, and a clay diyas and festive decor website maker. Before Diwali, feature your festive range on the home page and post a banner made with the Banner Ads Maker.',
        ],
      },
    ],
  },
  'gifts-hampers-website': {
    title: 'A website for gift hampers and personalised gifts',
    lead: 'A gift hamper online store builder for India, with personalisation built in.',
    sections: [
      {
        h: 'Hampers and gift baskets',
        p: [
          'Sell dry fruit hampers with a festive website maker, or run a custom gift basket ordering website: add each hamper with its sizes and prices, and share a festive coupon.',
        ],
      },
      {
        h: 'Personalised gifts',
        p: [
          'As a personalized photo frame gifts website builder, a gifts website lets the customer add a name or message and upload photos when they order. You set this in More options on each product.',
        ],
      },
    ],
  },
  'grocery-website': {
    title: 'Create an online grocery shop website',
    lead: 'Create an online grocery shop website free for 14 days: sell by weight, set a delivery charge and take UPI or Cash on Delivery orders from your area.',
    sections: [
      {
        h: 'Kirana, vegetables and dairy',
        p: [
          'A local kirana store online order website lets regular customers order from home. It also works as a fresh vegetable delivery store website and a dairy and milk delivery website builder. Add sizes by weight (250 g, 500 g, 1 kg), each with its own price.',
        ],
      },
      {
        h: 'Homemade and organic products',
        p: [
          'Use it as a pickle and papad selling website maker, a namkeen and snacks online shop maker, an organic spices business online store, an organic honey business website builder, a cold pressed oil online store maker, a dry fruits online store website maker or an online tea store website builder in India.',
        ],
      },
    ],
  },
  'bakery-cakes-website': {
    title: 'A website for cakes, sweets and homemade food',
    lead: 'A cake shop website builder for India, for home bakers, sweet shops, cloud kitchens and tiffin services.',
    sections: [
      {
        h: 'Cakes and sweets',
        p: [
          'A home baker cake order booking website lets customers choose the cake and its weight, then pay. It works as an online mithai sweet shop website maker and a homemade chocolates online shop builder too.',
        ],
      },
      {
        h: 'Homemade food, cloud kitchens and tiffin',
        p: [
          'Make a website for homemade food delivery free for 14 days. It is a cloud kitchen online ordering website and a tiffin service online order website builder: add each meal or plan as a product, set a delivery charge for your area, and get each order on WhatsApp.',
        ],
      },
    ],
  },
  'beauty-cosmetics-website': {
    title: 'A website for skincare and handmade cosmetics',
    lead: 'A skincare brand website builder for India, for organic soaps, handmade cosmetics and ayurvedic products.',
    sections: [
      {
        h: 'Soaps, skincare and ayurvedic products',
        p: [
          'Use it as an organic soap business website builder, a handmade cosmetics online store you can try free, or an ayurvedic herbal products website maker. Write the ingredients and how to use each product in its description, and use sizes for pack sizes like 50 ml and 100 ml.',
        ],
      },
    ],
  },
  'pet-supplies-website': {
    title: 'A website for pet food and supplies',
    lead: 'A pet food and supplies website builder for pet shops and home sellers.',
    sections: [
      {
        h: 'Pet food, toys and accessories',
        p: [
          'Add pet food by weight, and toys and accessories by size, each with its own price. Customers order from your link and you get every order on WhatsApp.',
        ],
      },
    ],
  },
  'footwear-website': {
    title: 'How to make an online footwear store',
    lead: 'If you are asking how to make an online footwear store, start here: shoe sizes, colours and UPI checkout are ready.',
    sections: [
      {
        h: 'Juttis, chappals and leather shoes',
        p: [
          'Sell handcrafted juttis online in India, use it as a Kolhapuri chappal website maker, or as a leather shoes retail store website builder. Add shoe sizes (6, 7, 8, 9) and colours to each pair.',
        ],
      },
    ],
  },
  'phone-cases-website': {
    title: 'A website for a phone cases business',
    lead: 'A website for a custom phone cases business and a printed mobile covers online shop maker.',
    sections: [
      {
        h: 'Cases for every phone model',
        p: [
          'Add the phone models as the options of each case and name the option Model, so customers pick their phone before they order. Post new designs on Instagram with the product link.',
        ],
      },
    ],
  },
};

// A niche page's article: its own sections, then the steps, payments and orders that every business shares
export function nicheArticle(niche) {
  const own = NICHE_ARTICLES[niche.slug];
  if (!own) return null;
  const name = niche.shortName.toLowerCase();
  return {
    id: 'niche-article',
    ...own,
    sections: [
      ...own.sections,
      {
        h: `Start your ${name} website in three steps`,
        p: [
          `First, open the ${name} demo and start a 14-day free trial: type your store name, WhatsApp number and email, and enter the code we email you. Your ${name} store is live in about a minute, with a home page, categories, sample products, a cart and checkout already in place.`,
          `Next, replace the sample products with your own. Each product gets up to three photos, a name, an original and a discounted price, and sizes or options with their own price where you need them. Then put your store link in your Instagram bio, your WhatsApp status and your Google Business profile. The [User Guide](/guide) shows every step with pictures.`,
        ],
      },
      {
        h: 'Get paid by UPI and get every order on WhatsApp',
        p: [
          'Customers pay by UPI with Google Pay, PhonePe, Paytm or any UPI app, or choose Cash on Delivery. With manual UPI the money goes straight to your own UPI ID, with no gateway fee; connect your own Razorpay account if you also want cards, net banking and automatic payment confirmation.',
          'Every new order is sent to your WhatsApp with the items, the address and the payment, so you never copy details from chats again. Update the status as you confirm, pack and ship, and your customer sees it in My orders.',
        ],
      },
      {
        h: 'Your own website, not a marketplace',
        p: [
          `On a marketplace your ${name} products sit next to hundreds of others and every sale pays a commission. Your own Ownezzy website shows only your products, under your own name, and takes 0% commission. You keep your customers, their contact details and every rupee of your sales, for ₹399 a month or ₹4,499 a year after the free trial.`,
        ],
      },
    ],
  };
}
